import React, { FC, useState, useEffect, useRef } from 'react';
import format from 'date-fns/format';
import ReactJson from '@microlink/react-json-view';
import { useQuery } from '@apollo/react-hooks';
import { PRODUCT_EDIT_HISTORY_QUERY } from 'graphql/queries';
import { IFoodlaCategoryOption } from 'models/category';
import { IFoodlaCategory } from 'models/product';
import {
  isEgg,
  isFruktOrGreen,
  isBeverages,
  isFish,
  isFoodSupplements,
  isSpecialFoodNutrition,
  isFeedCategory,
  isNewFood,
  isNewNonFood,
  isToy,
  isElectric,
  isJewelry,
  isGoods,
  isSewingKnittingYarn,
  isMedical,
  isChemical,
  isDetergent,
  isCosmetic,
  isComplementarySupplements,
  isSunscreen,
  isFeedMaterial,
  isFeedMixtures,
  isCompleteFeed,
  isOTC,
  isOtherFoodstuffs,
  isWhiteSnus,
  isElectricLamp,
  isElectricBattery,
  isSnusOrTobacco,
  isPharma,
  isCmrHazardCategory,
} from '../ProductUpdateForm/fields/category';

import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
  mainHeading: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  editHeading: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 500,
  },
  accordionDetails: {
    flexDirection: 'column',
    padding: theme.spacing(1, 2, 2),
  },
  rowContainer: {
    paddingBottom: theme.spacing(1.5),
    paddingTop: theme.spacing(1.5),
  },
  badge: {
    fontSize: '10px',
    fontWeight: 'bold',
    padding: '2px 6px',
    borderRadius: '4px',
    textTransform: 'uppercase',
    display: 'inline-block',
    marginLeft: theme.spacing(1.5),
  },
  createBadge: {
    backgroundColor: '#e6f4ea',
    color: '#137333',
  },
  updateBadge: {
    backgroundColor: '#e8f0fe',
    color: '#1a73e8',
  },
  valueContainer: {
    paddingLeft: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  columnLeft: {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: theme.spacing(2),
  },
  columnRight: {
    paddingLeft: theme.spacing(2),
  },
  accordionMain: {
    marginTop: theme.spacing(2),
  },
  progressContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(2, 0),
  },
  errorMessage: {
    color: 'red',
    padding: theme.spacing(1, 0),
  },
  emptyMessage: {
    color: '#aaa',
    fontStyle: 'italic',
    padding: theme.spacing(1, 0),
  },
  historyWrapper: {
    width: '100%',
  },
  innerAccordion: {
    marginBottom: theme.spacing(1),
    boxShadow: 'none',
    border: '1px solid #e0e0e0',
  },
  changeCount: {
    marginLeft: 'auto',
    color: '#666',
    fontSize: '13px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  headerText: {
    fontWeight: 'bold',
  },
  noChanges: {
    color: '#aaa',
    fontStyle: 'italic',
  },
  popoverCard: {
    position: 'fixed',
    transform: 'translate(-50%, -100%)',
    backgroundColor: 'rgba(33, 33, 33, 0.98)',
    color: '#fff',
    padding: '10px 16px',
    borderRadius: '8px',
    fontSize: '14px',
    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    zIndex: 99999,
    pointerEvents: 'none',
    boxShadow: '0px 8px 24px rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.15)',
    lineHeight: 1.4,
  },
}));

const useTooltipStyles = makeStyles(() => ({
  container: {
    minWidth: '240px',
    lineHeight: 1.4,
  },
  title: {
    fontWeight: 'bold',
    fontSize: '14px',
    marginBottom: '8px',
    color: '#fff',
  },
  sectionTitle: {
    fontSize: '13px',
    color: '#bbb',
  },
  sectionTitleAnd: {
    fontSize: '13px',
    color: '#bbb',
    marginTop: '6px',
  },
  indent: {
    fontSize: '13px',
    paddingLeft: '8px',
  },
  comma: {
    color: '#bbb',
  },
  categoriesWrapper: {
    fontSize: '13px',
    color: '#bbb',
    lineHeight: 1.4,
  },
  rolesWrapper: {
    fontSize: '13px',
    color: '#bbb',
    lineHeight: 1.4,
    marginTop: '5px',
  },
}));

const FIELD_UI_TITLES: Record<string, string> = {
  image_src: 'Produktbild för e-handel',
  additionalImages: 'Produktbild för e-handel',
  foodlaCategory: 'Kategori',
  icaCategoryId: 'ICA Kategori',
  icaOnlineCategoryId: 'ICA Online Kategori',
  coopCategoryId: 'Coop Kategori',
  woltOnlineCategoryId: 'Wolt Online Kategori',
  woltInternalCategoryId: 'Wolt Intern Kategori',
  apoteaCategoryId: 'Apotea Kategori',
  classification: 'Klassificering',
  alcoholPercentage: 'Alkoholhalt',
  recyclingType: 'Panttyp',
  categoryWeightloss: 'Ange kategori om produkten är för viktminskning',
  title: 'Produktnamn / Beteckning',
  disableCapitalizeTitle: 'Bevara exakt formatering (inaktivera automatisk versalisering)',
  alternativeTitle: 'Alternativ titel',
  EAN: 'EAN-kod (13 eller 8 siffror)',
  EAN2: 'EAN med justerade vikt/pris-siffror och/eller justerad kontrollsiffra',
  ignoreEANLengthError: 'Ignore EAN length error',
  purchasingDataSupplierArticleNo: 'Leverantörens artikelnummer',
  purchasingDataIncoterm: 'Leveransvillkor',
  purchasingDataPickupLocation: 'Hämtplats',
  eggPackerApprovalNumber: 'Godkännandenummer för äggpackeri',
  short_text: 'Produktbeskrivning',
  shortSalesDescription: 'Kort säljande beskrivning',
  animalFeedType: 'Fodertyp',
  ingredient_statement: 'Ingrediensförteckning',
  fishProductionMethod: 'Produktionsmetod',
  fishCatchMethod: 'Fångstmetod',
  catchArea: 'Fångstområde',
  feedAdditives: 'Fodertillsatser',
  analyticalConstituentsFeed: 'Analytiska beståndsdelar',
  feedingInstructions: 'Utfodringsanvisningar',
  animalAgeGroup: 'Djuråldersgrupp',
  non_food_ingredients: 'Kemiska ingredienser',
  labels: 'Märkningar',
  energi_kj: 'Energi kJ',
  energi_kcal: 'Energi kcal',
  fett: 'Fett',
  mattatFett: 'Varav mättat fett',
  enkelomattatFett: 'Varav enkelomättat fett',
  flerromattatFett: 'Varav fleromättat fett',
  kolhydrat: 'Kolhydrater',
  sockerarter: 'Varav sockerarter',
  polyoler: 'Varav polyoler',
  starkelse: 'Varav stärkelse',
  fiber: 'Fiber',
  protein: 'Protein',
  salt: 'Salt',
  nutritional_unit: 'Näringsenhet',
  vitaminA: 'Vitamin A',
  vitaminD: 'Vitamin D',
  vitaminE: 'Vitamin E',
  vitaminK: 'Vitamin K',
  vitaminC: 'Vitamin C',
  tiamin: 'Tiamin',
  riboflavin: 'Riboflavin',
  niacin: 'Niacin',
  vitaminB6: 'Vitamin B6',
  folsyra: 'Folsyra',
  vitaminB12: 'Vitamin B12',
  biotin: 'Biotin',
  pantotensyra: 'Pantotensyra',
  kalium: 'Kalium',
  klorid: 'Klorid',
  kalcium: 'Kalcium',
  fosfor: 'Fosfor',
  magnesium: 'Magnesium',
  jarn: 'Järn',
  zink: 'Zink',
  koppar: 'Koppar',
  mangan: 'Mangan',
  fluorid: 'Fluorid',
  selen: 'Selen',
  krom: 'Krom',
  molybden: 'Molybden',
  jod: 'Jod',
  burningTime: 'Brinntid',
  yarnWeight: 'Garnvikt',
  yarnRunLength: 'Löplängd',
  recommendedCrochetHook: 'Rekommenderad virknål',
  recommendedKnittingNeedles: 'Rekommenderade stickor',
  knittingGaugeStitches: 'Stickfasthet maskor',
  knittingGaugeRows: 'Stickfasthet varv',
  crochetGaugeStitches: 'Virkfasthet maskor',
  crochetGaugeRows: 'Virkfasthet varv',
  washingTemperature: 'Tvättemperatur',
  washingInstructions: 'Tvättråd',
  ironing: 'Strykning',
  tumbleDrying: 'Torktumling',
  dryCleaning: 'Kemtvätt',
  chlorineBleaching: 'Klorblekning',
  whiteSnusNicotinePerPortion: 'Nikotin per portion',
  whiteSnusNicotinePerGram: 'Nikotin per gram',
  whiteSnusNicotineStrength: 'Nikotinstyrka',
  whiteSnusFlavor: 'Smak',
  whiteSnusPortionsPerUnit: 'Portioner per dosa',
  whiteSnusWeightPerPortion: 'Vikt per portion',
  whiteSnusFormat: 'Format',
  lampEffect: 'Lampeffekt',
  lampModel: 'Lampmodell',
  lampEnergyClass: 'Energiklass',
  lampTechnology: 'Ljusteknik',
  lampSocketType: 'Sockel',
  lampDimmable: 'Dimbar',
  lampEquivalentBulb: 'Motsvarande glödlampa',
  lampBrightnessLm: 'Ljusflöde lm',
  lampColorTemperatureK: 'Färgtemperatur K',
  lampAverageLifespanH: 'Genomsnittlig livslängd h',
  lampColorRenderingRa: 'Färgåtergivning Ra',
  lampEnergyConsumption: 'Energiförbrukning',
  lampBeamAngleDeg: 'Spridningsvinkel grader',
  batteryModel: 'Batterimodell',
  batteryType: 'Batterityp (Storlek)',
  batteryTypeOther: 'Annan batterityp',
  batteryVoltage: 'Spänning',
  batteryTechnology: 'Kemisk sammansättning (Batteriteknik)',
  batteryCapacity: 'Kapacitet',
  batteryLife: 'Batteritid',
  batteryWeight: 'Batteriets vikt',
  batteryRechargeable: 'Laddningsbar',
  manufacturingLocation: 'Tillverkningsort',
  batteryHazardousMetals: 'Innehåller kvicksilver, kadmium eller bly',
  descriptive_size_amount: 'Storleksbeskrivning mängd',
  descriptive_size_amount_extra: 'Ytterligare storleksbeskrivning',
  descriptive_size_unit: 'Storleksbeskrivning enhet',
  trade_item_temperature_information: 'Temperaturinformation',
  place_of_item_activity: 'Ursprungsland och härkomstplats',
  additional_required_information: 'Ytterligare obligatoriska uppgifter',
  location: 'Adress till producent',
  quantity_in_stock: 'Lagersaldo',
  consumer_size_to_order_size_ratio: 'Antal per beställningsenhet',
  approve: 'Godkännande',
  color: 'Färg',
  material: 'Material',
  dimensions: 'Mått',
  capacity: 'Kapacitet',
  imageUnderProcess: 'Bild under bearbetning',
  salesType: 'Försäljningstyp',
  gross_weight_num: 'Bruttovikt',
  gross_height: 'Bruttohöjd',
  gross_width: 'Bruttobredd',
  gross_depth: 'Bruttodjup',
  min_temperature: 'Min temperatur',
  max_temperature: 'Max temperatur',
  preparedForm: 'Beredningsform',
  dosageAndUsageInstructions: 'Dosering och användningsinstruktioner',
  signalWords: 'Signalord',
  hazardSymbols: 'Farosymboler',
  hazardStatements: 'Faroangivelser',
  hazardStatementFills: 'Faroangivelse fyllningar',
  securityNotices: 'Skyddsangivelser',
  securityNoticeFills: 'Skyddsangivelse fyllningar',
  wasteManagementAndRecyclingInformation: 'Avfallshantering och återvinning',
  ceMarking: 'Är produkten CE-märkt?',
  specifyCEStandards: 'Ange mot vilka standarder/krav',
  plasticTrayPackaging: 'Levereras produkten i plasttråg?',
  compliancePpwrPackaging: 'Uppfyller produktens förpackning kraven i PPWR (EU 2025/40)?',
  shrinkWrapPackaging: 'Levereras produkten inplastad, i krympplast eller shrinkwrap?',
  bestBeforeDate: 'Har produkten ett bäst före datum?',
  containsBattery: 'Innehåller produkten något batteri?',
  compliancePops:
    'Uppfyller produkten de krav som finns i förordning (EU) 2019/1021 om långlivade organiska föroreningar (POPs)?',
  containsBannedSubstancesPops:
    'Innehåller produkten något av de förbjudna ämnen som är upptagna i bilaga I i förordning (EU) 2019/1021?',
  cmrSubstance: 'Innehåller produkten CMR-klassade ämnen?',
  CMRSubstances: 'CMR-klassat ämne',
  reachSubstance: 'Innehåller produkten ett ämne som är förbjudet eller begränsat i REACH-förordningen?',
  regulatedSubstance: 'REACH-ämnen',
  complianceMetalsSkinContact:
    'Uppfyller produkten reglerna om skadliga tungmetaller i bilaga XVII till Reach-förordningen?',
  confirmGuidanceSwedishElectronics:
    'Om produkten är elektrisk/elektronisk, finns det svensk bruksanvisning bifogad på förpackningen?',
  complianceToyDirective: 'Uppfyller produkten som är en leksak kraven i leksaksdirektivet (EG) 2009/48?',
  complianceBiocid: 'Uppfyller varan som är en biocidprodukt kraven i EUs biocidförordning 528/2012?',
  complianceBiocidalTreated: 'Uppfyller varan som är biocidbehandlad kraven i EUs biocidförordning 528/2012?',
  hazardousSubstancesOver1:
    'Innehåller produkten något ämne på EU:s kandidatlista i en mängd som överstiger 0,1 viktprocent?',
  informationHazardousSubstances: 'Lista särskilt farliga ämnen och information för säker användning',
  complianceProductSafety2004451: 'Följer produkten Produktsäkerhetslagen 2004:451?',
  inactive: 'Inaktiv',
  isDraft: 'Utkast',
  createdBy: 'Skapad av',
  childrenUnder16: 'Är produkten avsedd för barn under 16 år?',
  country_of_manufacturing: 'Tillverkningsland',
  additionalCharacteristics: 'Ytterligare egenskaper',
  brand: 'Varumärke',
  brand_food: 'Varumärke',
  nameNordicDistributor: 'Namn på nordisk distributör',
  consumerContactCompanyName: 'Konsumentkontakt företagsnamn',
  consumerContactPhone: 'Konsumentkontakt telefon',
  consumerContactEmail: 'Konsumentkontakt e-post',
  consumerContactWebsite: 'Konsumentkontakt webbplats',
  consumerContactAddress: 'Konsumentkontakt adress',
  exportConsumerContact: 'Exportera konsumentkontakt',
  search_terms: 'Söktermer',
  apoteaListPrice: 'Listpris',
  apoteaPurchasePrice: 'Apoteas inköpspris',
  apoteaStorePrice: 'Rekommenderat utpris i SEK (inkl. moms)',
  apoteaStorePriceNok: 'Rekommenderat utpris i NOK (inkl. moms)',
  consumerPackOrderableUnit: 'Beställningsbar konsumentförpackning',
  consumerPackDespatchUnit: 'Leveransbar konsumentförpackning',
  consumerPackNetWeight: 'Konsumentförpackning nettovikt',
  storePackEANForStorePack: 'EAN-kod distributionsförpackning (14 siffror)',
  storePackOrderableUnit: 'D-pack beställningsbar',
  storePackDespatchUnit: 'D-pack leveransbar',
  storePackTradeItemMeasurementsLengthDepth: 'D-pack längd/djup',
  storePackTradeItemMeasurementsWidth: 'D-pack bredd',
  storePackTradeItemMeasurementsHeight: 'D-pack höjd',
  storePackGrossWeight: 'D-pack bruttovikt',
  transportEANForStorePack: 'EAN-kod transportförpackning (14 siffror)',
  transportQtyOfBaseItem: 'Mellankolli antal basartiklar',
  transportQtyOfNextLowerItem: 'Mellankolli antal underliggande',
  transportOrderableUnit: 'Mellankolli beställningsbar',
  transportDespatchUnit: 'Mellankolli leveransbar',
  transportTradeItemMeasurementsLengthDepth: 'Mellankolli längd/djup',
  transportTradeItemMeasurementsWidth: 'Mellankolli bredd',
  transportTradeItemMeasurementsHeight: 'Mellankolli höjd',
  transportGrossWeight: 'Mellankolli bruttovikt',
  transportMinTemperature: 'Mellankolli min temperatur',
  transportMaxTemperature: 'Mellankolli max temperatur',
  palletLayerEANForStorePack: 'EAN/GTIN for store pack',
  palletLayerQtyOfBaseItem: 'Lav antal basartiklar',
  palletLayerQtyOfNextLowerItem: 'Lav antal underliggande',
  palletLayerOrderableUnit: 'Lav beställningsbar',
  palletLayerDespatchUnit: 'Lav leveransbar',
  palletLayerTradeItemMeasurementsLengthDepth: 'Lav längd/djup',
  palletLayerTradeItemMeasurementsWidth: 'Lav bredd',
  palletLayerTradeItemMeasurementsHeight: 'Lav höjd',
  palletLayerGrossWeight: 'Lav bruttovikt',
  palletQtyOfStorePack: 'Antal distributionsförpackningar per pall (dfp/pall)',
  palletEANForStorePack: 'EAN-kod pall (14 siffror)',
  palletQtyOfBaseItem: 'Antal konsumentförpackningar per pall (kfp/pall) / Qty of base item',
  palletQtyOfNextLowerItem: 'Qty of next lower item / Antal transportförpackningar per pall (tfp/pall)',
  palletOrderableUnit: 'Pall beställningsbar',
  palletDespatchUnit: 'Pall leveransbar',
  palletTradeItemMeasurementsLengthDepth: 'Pall längd/djup',
  palletTradeItemMeasurementsWidth: 'Pall bredd',
  palletTradeItemMeasurementsHeight: 'Pall höjd',
  palletGrossWeight: 'Pall bruttovikt',
  lavQuantity: 'Lavkvantitet',
  commodityCode: 'Tulltaxenummer',
  basicDataTaxClassification: 'Produktens skattesats',
  basicDataAvailabilityDateFrom: 'Lanseringsdag mot konsument för nyheter',
  articleFirstOrderDate: 'Första beställningsdag för nyheter',
  articleFirstDelivery: 'Första leveransdag för nyheter',
  priceListPriceCurrency: 'List price currency',
  priceListPrice: 'List price',
  priceRecommendedConsumerPrice: 'Recommended consumer price(Retail price Large)',
  qrLabelingSwedish: 'Svensk märkning',
  qrMedicalClaims: 'Medicinska påståenden',
  qrMarketingClaimsCosmetic: 'Marknadsföringspåståenden kosmetika',
  qrEfsaUlValues: 'EFSA UL-värden',
  qrNutritionClaims: 'Näringspåståenden',
  qrNutritionClaimsCode: 'Näringspåståenden',
  qrHealthClaims: 'Hälsopåståenden',
  qrHealthClaimsCode: 'Hälsopåståenden',
  qrFoodLabeling: 'Livsmedelsmärkning',
  qrMisleadingSalesTerms: 'Vilseledande försäljningstermer',
  qrFoodHygiene: 'Livsmedelshygien',
  qrClassificationLabelingPackaging: 'Klassificering märkning förpackning',
  qrElectronic: 'Elektronisk märkning',
  qrElectronicCode: 'Elektronisk märkning',
  qrAdditivesInFeed: 'Tillsatser i foder',
  qrFeedLabeling: 'Fodermärkning',
  qrRelevantFeedCertifications: 'Relevanta fodercertifikat',
  adminStatus: 'Godkännandestatus',
};

const FIELD_CATEGORIES: Record<string, string[]> = {
  classification: ['Frukter, bär och grönsaker'],
  ingredient_statement: ['Alla livsmedel'],
  alcoholPercentage: ['Drycker'],
  recyclingType: ['Drycker'],
  recycling_amount: ['Drycker'],
  preparedForm: ['Kosttillskott enligt definitionen i direktiv 2002/46/EG', 'Livsmedel för särskilda näringsändamål'],
  dosageAndUsageInstructions: [
    'Kosttillskott enligt definitionen i direktiv 2002/46/EG',
    'Livsmedel för särskilda näringsändamål',
  ],
  categoryWeightloss: ['Livsmedel för särskilda näringsändamål'],
  eggWeightClass: ['Ägg och äggprodukter'],
  eggPackerApprovalNumber: ['Ägg och äggprodukter'],
  fishProductionMethod: ['Fisk och fiskeriprodukter', 'Egen fiskdisk'],
  fishCatchMethod: ['Fisk och fiskeriprodukter', 'Egen fiskdisk'],
  catchArea: ['Fisk och fiskeriprodukter', 'Egen fiskdisk'],
  animalFeedType: ['Kompletteringsfoder & Fodertillskott', 'Foderråvara', 'Foderblandningar', 'Helfoder'],
  feedAdditives: ['Kompletteringsfoder & Fodertillskott', 'Foderråvara', 'Foderblandningar', 'Helfoder'],
  analyticalConstituentsFeed: ['Kompletteringsfoder & Fodertillskott', 'Foderråvara', 'Foderblandningar', 'Helfoder'],
  feedingInstructions: ['Kompletteringsfoder & Fodertillskott', 'Foderråvara', 'Foderblandningar', 'Helfoder'],
  animalAgeGroup: ['Kompletteringsfoder & Fodertillskott', 'Foderråvara', 'Foderblandningar', 'Helfoder'],
  non_food_ingredients: ['Tvättmedel/Diskmedel', 'Kemiska produkter', 'Kosmetiska produkter'],
  whiteSnusNicotinePerPortion: ['Vitt snus'],
  whiteSnusNicotinePerGram: ['Vitt snus'],
  whiteSnusNicotineStrength: ['Vitt snus'],
  whiteSnusFlavor: ['Vitt snus'],
  whiteSnusPortionsPerUnit: ['Vitt snus'],
  whiteSnusWeightPerPortion: ['Vitt snus'],
  whiteSnusFormat: ['Vitt snus'],
  lampEffect: ['Elektrisk', 'Lampor'],
  lampModel: ['Elektrisk', 'Lampor'],
  lampEnergyClass: ['Elektrisk', 'Lampor'],
  lampTechnology: ['Elektrisk', 'Lampor'],
  lampSocketType: ['Elektrisk', 'Lampor'],
  lampDimmable: ['Elektrisk', 'Lampor'],
  lampEquivalentBulb: ['Elektrisk', 'Lampor'],
  lampBrightnessLm: ['Elektrisk', 'Lampor'],
  lampColorTemperatureK: ['Elektrisk', 'Lampor'],
  lampAverageLifespanH: ['Elektrisk', 'Lampor'],
  lampColorRenderingRa: ['Elektrisk', 'Lampor'],
  lampEnergyConsumption: ['Elektrisk', 'Lampor'],
  lampBeamAngleDeg: ['Elektrisk', 'Lampor'],
  batteryModel: ['Elektrisk', 'Batterier'],
  batteryType: ['Elektrisk', 'Batterier'],
  batteryTypeOther: ['Elektrisk', 'Batterier'],
  batteryVoltage: ['Elektrisk', 'Batterier'],
  batteryTechnology: ['Elektrisk', 'Batterier'],
  batteryCapacity: ['Elektrisk', 'Batterier'],
  batteryLife: ['Elektrisk', 'Batterier'],
  batteryWeight: ['Elektrisk', 'Batterier'],
  batteryRechargeable: ['Elektrisk', 'Batterier'],
  manufacturingLocation: ['Elektrisk', 'Batterier'],
  batteryHazardousMetals: ['Elektrisk', 'Batterier'],
  country_of_manufacturing: ['Alla livsmedel utom Ägg'],
  brand_food: ['Alla livsmedel'],
  brand: ['Alla non-food'],
  childrenUnder16: [
    'Alla livsmedel',
    'Leksaker',
    'Elektrisk',
    'Smycken',
    'Varor',
    'Kemiska produkter',
    'Tvättmedel/Diskmedel',
    'Kosmetiska produkter',
    'Solskydd',
    'Receptfria läkemedel',
  ],
  ceMarking: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk', 'Medicinteknik'],
  specifyCEStandards: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk', 'Medicinteknik'],
  containsBattery: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk', 'Medicinteknik'],
  compliancePops: ['Varor', 'Kemiska produkter', 'Tvättmedel/Diskmedel'],
  containsBannedSubstancesPops: ['Varor', 'Kemiska produkter', 'Tvättmedel/Diskmedel'],
  complianceMetalsSkinContact: ['Smycken'],
  confirmGuidanceSwedishElectronics: ['Leksaker', 'Elektrisk', 'Medicinteknik'],
  complianceToyDirective: ['Leksaker', 'Elektrisk'],
  complianceBiocid: ['Leksaker', 'Kemiska produkter', 'Tvättmedel/Diskmedel'],
  complianceBiocidalTreated: ['Leksaker', 'Elektrisk', 'Smycken', 'Varor'],
  hazardousSubstancesOver1: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk'],
  informationHazardousSubstances: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk'],
  complianceProductSafety2004451: ['Varor', 'Smycken', 'Leksaker', 'Elektrisk'],
};

const FIELD_ROLES: Record<string, string[]> = {
  disableCapitalizeTitle: ['Superuser'],
  ignoreEANLengthError: ['Superuser'],
  exportConsumerContact: ['Superuser'],
  imageUnderProcess: ['Superuser'],
  adminStatus: ['Superuser'],
  childrenUnder16: ['Leverantör'],
  apoteaListPrice: ['Superuser', 'Apotea-leverantör'],
  apoteaPurchasePrice: ['Superuser', 'Apotea-leverantör'],
  apoteaStorePrice: ['Superuser', 'Apotea-leverantör'],
  apoteaStorePriceNok: ['Superuser', 'Apotea-leverantör'],
  bestBeforeDate: ['Leverantör'],
  plasticTrayPackaging: ['Leverantör'],
  compliancePpwrPackaging: ['Leverantör'],
  shrinkWrapPackaging: ['Leverantör'],
};

interface IReactJsonCopy {
  src: unknown;
}

/**
 * Represents a single history entry for a product field change.
 */
interface IHistoryEntry {
  versionCreated: string | number;
  value?: unknown;
  type?: string;
}

/**
 * Extracts and reconstructs the latest `foodlaCategory` object from the product edit history.
 *
 * Handles multiple representations of the category value safely:
 * - Formatted path string (e.g. "Root -> Subcategory -> Leaf")
 * - Direct leaf name or category ID string
 * - Object representation (e.g. `{ id: string, name: string }`)
 * - Non-string / malformed values (`null`, `undefined`, `{}`, arrays, numbers) without throwing runtime errors.
 *
 * @param rawHistory - The `productEditHistory` dictionary containing field change entries.
 * @param categories - Flat list of available categories used for resolving ID and name mappings.
 * @returns The resolved `IFoodlaCategory` object or `undefined` if no category could be determined.
 */
export const extractFoodlaCategoryFromHistory = (
  rawHistory: Record<string, unknown> | undefined,
  categories: IFoodlaCategory[] | undefined
): IFoodlaCategory | undefined => {
  if (!rawHistory) return undefined;

  let currentCategoryValue: unknown = undefined;
  let newestTimestamp = 0;

  for (const [field, entries] of Object.entries(rawHistory)) {
    if (field === 'foodlaCategory' && Array.isArray(entries)) {
      for (const entry of entries) {
        const casted = entry as IHistoryEntry;
        const t = Number(casted?.versionCreated);
        if (t > newestTimestamp) {
          newestTimestamp = t;
          currentCategoryValue = casted?.value;
        }
      }
    }
  }

  if (currentCategoryValue === undefined || currentCategoryValue === null) {
    return undefined;
  }

  // Case 1: Value is a string (e.g. "Root -> Sub -> Leaf" or "Leaf" or ID)
  if (typeof currentCategoryValue === 'string') {
    const trimmed = currentCategoryValue.trim();
    if (!trimmed) return undefined;

    const parts = trimmed.split(' -> ');
    const leafName = parts[parts.length - 1]?.trim();

    if (categories && categories.length > 0) {
      if (leafName) {
        const matched = categories.find(
          (c: IFoodlaCategory) => c.name === leafName || c.id === leafName || c.id === trimmed
        );
        if (matched) {
          return {
            id: matched.id,
            name: matched.name,
          };
        }
      }
    }

    if (leafName) {
      return {
        id: '',
        name: leafName,
      };
    }
  }

  // Case 2: Value is an object (e.g. { id: '...', name: '...' } or { name: '...' } or { _id: '...' })
  if (typeof currentCategoryValue === 'object' && currentCategoryValue !== null) {
    const catObj = currentCategoryValue as Record<string, any>;
    const catId = catObj.id || catObj._id;
    const catName = typeof catObj.name === 'string' ? catObj.name : undefined;

    if (categories && categories.length > 0) {
      const matched = categories.find(
        (c: IFoodlaCategory) => (catId && c.id === catId) || (catName && c.name === catName)
      );
      if (matched) {
        return {
          id: matched.id,
          name: matched.name,
        };
      }
    }

    if (catId || catName) {
      return {
        id: String(catId || ''),
        name: String(catName || ''),
      };
    }
  }

  return undefined;
};

interface ITooltipSpanElement extends HTMLSpanElement {
  __hasTooltipListeners?: boolean;
}

const checkCategoryFit = (
  categoryLabel: string,
  foodlaCategory: IFoodlaCategory | undefined,
  rootCategory: IFoodlaCategoryOption | undefined,
  producerIsStore: boolean | undefined
): boolean => {
  if (categoryLabel === 'Inte butiksproducent') {
    return !producerIsStore;
  }
  if (!foodlaCategory || !rootCategory) return false;

  switch (categoryLabel) {
    case 'Drycker':
      return !!isBeverages(foodlaCategory, rootCategory);
    case 'Frukter, bär och grönsaker':
      return !!isFruktOrGreen(foodlaCategory, rootCategory);
    case 'Kosttillskott enligt definitionen i direktiv 2002/46/EG':
      return !!isFoodSupplements(foodlaCategory, rootCategory);
    case 'Livsmedel för särskilda näringsändamål':
      return !!isSpecialFoodNutrition(foodlaCategory, rootCategory);
    case 'Ägg och äggprodukter':
      return !!isEgg(foodlaCategory, rootCategory);
    case 'Fisk och fiskeriprodukter':
    case 'Egen fiskdisk':
      return !!isFish(foodlaCategory, rootCategory);
    case 'Kompletteringsfoder & Fodertillskott':
      return !!isComplementarySupplements(foodlaCategory, rootCategory);
    case 'Foderråvara':
      return !!isFeedMaterial(foodlaCategory, rootCategory);
    case 'Foderblandningar':
      return !!isFeedMixtures(foodlaCategory, rootCategory);
    case 'Helfoder':
      return !!isCompleteFeed(foodlaCategory, rootCategory);
    case 'Tvättmedel/Diskmedel':
      return !!isDetergent(foodlaCategory, rootCategory);
    case 'Kemiska produkter':
      return !!isChemical(foodlaCategory, rootCategory);
    case 'Kosmetiska produkter':
      return !!isCosmetic(foodlaCategory, rootCategory);
    case 'Vitt snus':
      return !!isWhiteSnus(foodlaCategory, rootCategory);
    case 'Elektrisk':
      return !!isElectric(foodlaCategory, rootCategory);
    case 'Lampor':
      return !!isElectricLamp(foodlaCategory, rootCategory);
    case 'Batterier':
      return !!isElectricBattery(foodlaCategory, rootCategory);
    case 'Leksaker':
      return !!isToy(foodlaCategory, rootCategory);
    case 'Smycken':
      return !!isJewelry(foodlaCategory, rootCategory);
    case 'Varor':
      return !!isGoods(foodlaCategory, rootCategory);
    case 'Medicinteknik':
      return !!isMedical(foodlaCategory, rootCategory);
    case 'Solskydd':
      return !!isSunscreen(foodlaCategory, rootCategory);
    case 'Receptfria läkemedel':
      return !!isOTC(foodlaCategory, rootCategory);
    case 'Alla livsmedel utom Ägg':
      return !!isNewFood(foodlaCategory, rootCategory) && !isEgg(foodlaCategory, rootCategory);
    case 'Alla livsmedel':
      return !!isNewFood(foodlaCategory, rootCategory);
    case 'Alla non-food':
      return !!isNewNonFood(foodlaCategory, rootCategory);
    default:
      return false;
  }
};

const checkCmrFieldsFit = (
  foodlaCategory: IFoodlaCategory | undefined,
  rootCategory: IFoodlaCategoryOption | undefined,
  producerIsStore: boolean | undefined
): boolean => {
  if (producerIsStore) return false;
  return (
    !!isPharma(foodlaCategory, rootCategory) ||
    !!isCmrHazardCategory(foodlaCategory, rootCategory) ||
    !isNewFood(foodlaCategory, rootCategory)
  );
};

const checkReachSubstanceFit = (
  foodlaCategory: IFoodlaCategory | undefined,
  rootCategory: IFoodlaCategoryOption | undefined,
  producerIsStore: boolean | undefined
): boolean => {
  if (producerIsStore) return false;
  return (
    !!isToy(foodlaCategory, rootCategory) ||
    !!isElectric(foodlaCategory, rootCategory) ||
    !!isJewelry(foodlaCategory, rootCategory) ||
    !!isGoods(foodlaCategory, rootCategory) ||
    !!isMedical(foodlaCategory, rootCategory) ||
    !!isChemical(foodlaCategory, rootCategory) ||
    !!isDetergent(foodlaCategory, rootCategory) ||
    !!isCosmetic(foodlaCategory, rootCategory) ||
    !!isSunscreen(foodlaCategory, rootCategory) ||
    !isNewFood(foodlaCategory, rootCategory)
  );
};

interface IProductEditHistoryProps {
  productId: string;
  rootCategory?: IFoodlaCategoryOption;
  categories?: IFoodlaCategory[];
  producerIsStore?: boolean;
  expanded?: boolean;
  onExpand?: (event: any, isExpanded: boolean) => void;
  highlightedField?: string;
}

const TooltipContent: FC<{
  text: string;
  swedishTitle: string;
  foodlaCategory?: IFoodlaCategory;
  rootCategory?: IFoodlaCategoryOption;
  producerIsStore?: boolean;
}> = ({ text, swedishTitle, foodlaCategory, rootCategory, producerIsStore }) => {
  const classes = useTooltipStyles();
  const categoriesList = FIELD_CATEGORIES[text];
  const allowedRoles = FIELD_ROLES[text];

  const fitStyle = (val: boolean) => ({
    fontWeight: val ? ('bold' as const) : ('normal' as const),
    color: val ? '#00e676' : '#aaa',
    opacity: val ? 1 : 0.65,
  });

  if (text === 'cmrSubstance' || text === 'CMRSubstances') {
    const pharmaVal = !!isPharma(foodlaCategory, rootCategory);
    const cmrHazardVal = !!isCmrHazardCategory(foodlaCategory, rootCategory);
    const isFoodVal = !!isNewFood(foodlaCategory, rootCategory);
    const isSuperuserVal = true;
    const notFoodVal = !isFoodVal;
    const isNotStoreVal = !producerIsStore;

    return (
      <div className={classes.container}>
        <div className={classes.title}>Villkor</div>
        <div className={classes.sectionTitle}>Krävs:</div>
        <div className={classes.indent} style={fitStyle(isNotStoreVal)}>
          Inte butiksproducent
        </div>
        <div className={classes.sectionTitleAnd}>OCH minst en av:</div>
        <div className={classes.indent}>
          <span style={fitStyle(pharmaVal)}>Kategori Läkemedel</span>
          <span className={classes.comma}>, </span>
          <span style={fitStyle(cmrHazardVal)}>CMR-farlighetskategori</span>
          <span className={classes.comma}>, </span>
          <span style={fitStyle(isSuperuserVal && notFoodVal)}>Superuser & Non-Food</span>
        </div>
      </div>
    );
  }

  if (text === 'reachSubstance' || text === 'regulatedSubstance') {
    const isToyVal = !!isToy(foodlaCategory, rootCategory);
    const isElectricVal = !!isElectric(foodlaCategory, rootCategory);
    const isJewelryVal = !!isJewelry(foodlaCategory, rootCategory);
    const isGoodsVal = !!isGoods(foodlaCategory, rootCategory);
    const isMedicalVal = !!isMedical(foodlaCategory, rootCategory);
    const isChemicalVal = !!isChemical(foodlaCategory, rootCategory);
    const isDetergentVal = !!isDetergent(foodlaCategory, rootCategory);
    const isCosmeticVal = !!isCosmetic(foodlaCategory, rootCategory);
    const isSunscreenVal = !!isSunscreen(foodlaCategory, rootCategory);

    const isFoodVal = !!isNewFood(foodlaCategory, rootCategory);
    const isSuperuserVal = true;
    const notFoodVal = !isFoodVal;
    const isNotStoreVal = !producerIsStore;

    const restrictedCatVal =
      isToyVal ||
      isElectricVal ||
      isJewelryVal ||
      isGoodsVal ||
      isMedicalVal ||
      isChemicalVal ||
      isDetergentVal ||
      isCosmeticVal ||
      isSunscreenVal;
    const superuserNonFoodVal = isSuperuserVal && notFoodVal;

    return (
      <div className={classes.container}>
        <div className={classes.title}>Villkor</div>
        <div className={classes.sectionTitle}>Krävs:</div>
        <div className={classes.indent} style={fitStyle(isNotStoreVal)}>
          Inte butiksproducent
        </div>
        <div className={classes.sectionTitleAnd}>OCH minst en av:</div>
        <div className={classes.indent}>
          <span style={fitStyle(restrictedCatVal)}>REACH-begränsad kategori</span>
          <span className={classes.comma}>, </span>
          <span style={fitStyle(superuserNonFoodVal)}>Superuser & Non-Food</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={classes.title}>{swedishTitle}</div>
      {categoriesList && (
        <div className={classes.categoriesWrapper}>
          Kategorier:{' '}
          {categoriesList.map((catLabel, index) => {
            const fit = checkCategoryFit(catLabel, foodlaCategory, rootCategory, producerIsStore);
            return (
              <React.Fragment key={catLabel}>
                {index > 0 && ', '}
                <span style={fitStyle(fit)}>{catLabel}</span>
              </React.Fragment>
            );
          })}
        </div>
      )}
      {allowedRoles && (
        <div className={classes.rolesWrapper}>
          Roller:{' '}
          {allowedRoles.map((roleLabel, index) => {
            let fit = false;
            if (roleLabel === 'Superuser') {
              fit = true;
            } else if (roleLabel === 'Leverantör') {
              fit = !producerIsStore;
            }
            return (
              <React.Fragment key={roleLabel}>
                {index > 0 && ', '}
                <span style={fitStyle(fit)}>{roleLabel}</span>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </>
  );
};

const ProductEditHistory: FC<IProductEditHistoryProps> = ({
  productId,
  rootCategory,
  categories = [],
  producerIsStore,
  expanded: propsExpanded,
  onExpand: propsOnExpand,
  highlightedField,
}) => {
  const classes = useStyles();
  const [localExpanded, setLocalExpanded] = useState(false);
  const isControlled = propsExpanded !== undefined;
  const expanded = isControlled ? propsExpanded : localExpanded;

  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredField, setHoveredField] = useState<{
    text: string;
    x: number;
    y: number;
    swedishTitle: string;
    foodlaCategory?: IFoodlaCategory;
  } | null>(null);

  const handleExpand = (event: any, isExpanded: boolean) => {
    if (propsOnExpand) {
      propsOnExpand(event, isExpanded);
    } else {
      setLocalExpanded(isExpanded);
    }
  };

  const { data, loading, error } = useQuery(PRODUCT_EDIT_HISTORY_QUERY, {
    variables: { id: productId },
    skip: !expanded || !productId,
  });

  useEffect(() => {
    if (!highlightedField || !containerRef.current || !expanded) return;

    const timer = setTimeout(() => {
      if (!containerRef.current) return;
      const spans = containerRef.current.querySelectorAll('span');
      let foundSpan: HTMLElement | null = null;

      for (const span of Array.from(spans)) {
        const text = span.textContent?.trim().replace(/^["']|["']$/g, '');
        if (text === highlightedField) {
          foundSpan = span as HTMLElement;
          break;
        }
      }

      if (foundSpan) {
        const row = (foundSpan.closest('.variable-row') || foundSpan.closest('div') || foundSpan) as HTMLElement;
        if (row) {
          row.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const originalBackground = row.style.backgroundColor;
          row.style.backgroundColor = 'rgba(255, 235, 59, 0.5)';
          row.style.transition = 'background-color 1.5s ease-out';

          const resetTimer = setTimeout(() => {
            row.style.backgroundColor = originalBackground;
          }, 2000);

          return () => clearTimeout(resetTimer);
        }
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [highlightedField, expanded]);

  useEffect(() => {
    if (!containerRef.current) return;

    const applyTooltips = () => {
      if (!containerRef.current) return;

      // Reconstruct foodlaCategory object using the flat categories array
      const foodlaCategory = extractFoodlaCategoryFromHistory(data?.productEditHistory, categories);

      const spans = containerRef.current.querySelectorAll('span');
      spans.forEach(span => {
        const text = span.textContent?.trim().replace(/^["']|["']$/g, '');
        if (text && FIELD_UI_TITLES[text]) {
          const htmlSpan = span as HTMLSpanElement;
          const swedishTitle = FIELD_UI_TITLES[text];

          // Set premium visual indicators
          htmlSpan.style.cursor = 'help';
          htmlSpan.style.borderBottom = '1px dotted #888';

          const categoriesList = FIELD_CATEGORIES[text];
          const rolesList = FIELD_ROLES[text];

          let fits = false;
          if (text === 'cmrSubstance' || text === 'CMRSubstances') {
            fits = checkCmrFieldsFit(foodlaCategory, rootCategory, producerIsStore);
          } else if (text === 'reachSubstance' || text === 'regulatedSubstance') {
            fits = checkReachSubstanceFit(foodlaCategory, rootCategory, producerIsStore);
          } else if (categoriesList || rolesList) {
            let categoryFits = true;
            if (categoriesList) {
              const hasCategoryMatch = categoriesList
                .filter(c => c !== 'Inte butiksproducent')
                .some(catLabel => checkCategoryFit(catLabel, foodlaCategory, rootCategory, producerIsStore));
              const hasNotStoreMatch = !categoriesList.includes('Inte butiksproducent') || !producerIsStore;
              categoryFits = hasCategoryMatch && hasNotStoreMatch;
            }

            let roleFits = true;
            if (rolesList) {
              roleFits = rolesList.some(roleLabel => {
                if (roleLabel === 'Superuser') {
                  return true;
                }
                if (roleLabel === 'Leverantör') {
                  return !producerIsStore;
                }
                if (roleLabel === 'Apotea-leverantör') {
                  return true;
                }
                return false;
              });
            }

            fits = categoryFits && roleFits;
          }

          if (
            categoriesList ||
            rolesList ||
            ['cmrSubstance', 'CMRSubstances', 'reachSubstance', 'regulatedSubstance'].includes(text)
          ) {
            if (fits) {
              htmlSpan.style.color = '#137333';
            } else {
              htmlSpan.style.color = '#888';
            }
          } else {
            htmlSpan.style.color = '';
          }

          // Bind high-performance event listeners
          const spanWithListener = htmlSpan as ITooltipSpanElement;
          if (!spanWithListener.__hasTooltipListeners) {
            spanWithListener.__hasTooltipListeners = true;

            htmlSpan.addEventListener('mouseenter', () => {
              const rect = htmlSpan.getBoundingClientRect();
              setHoveredField({
                text,
                x: rect.left + rect.width / 2,
                y: rect.top - 8,
                swedishTitle,
                foodlaCategory,
              });
            });

            htmlSpan.addEventListener('mouseleave', () => {
              setHoveredField(null);
            });
          }
        }
      });
    };

    applyTooltips();

    const observer = new MutationObserver(() => {
      applyTooltips();
    });

    observer.observe(containerRef.current, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
    };
  }, [data, expanded, categories, rootCategory, producerIsStore]);

  // Reconstruct chronological edits from backend fieldHistory
  const editsMap: Record<
    string,
    { timestamp: number; type: string; changes: Record<string, unknown>; previous: Record<string, unknown> }
  > = {};

  const rawHistory = data?.productEditHistory || {};
  for (const [field, entries] of Object.entries(rawHistory)) {
    if (!Array.isArray(entries)) continue;
    entries.forEach((entryItem, index: number) => {
      const entry = entryItem as IHistoryEntry;
      const timestampStr = String(entry.versionCreated);
      const timestampNum = Number(entry.versionCreated);

      if (!editsMap[timestampStr]) {
        editsMap[timestampStr] = {
          timestamp: timestampNum,
          type: entry.type || 'UPDATE',
          changes: {},
          previous: {},
        };
      }
      editsMap[timestampStr].changes[field] = entry.value;

      const nextOlder = entries[index + 1];
      if (nextOlder) {
        const castedOlder = nextOlder as IHistoryEntry;
        editsMap[timestampStr].previous[field] = castedOlder.value;
      }
    });
  }

  const sortedEdits = Object.values(editsMap).sort((a, b) => b.timestamp - a.timestamp);

  return (
    <div ref={containerRef}>
      <Accordion expanded={expanded} onChange={handleExpand} className={classes.accordionMain}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography className={classes.mainHeading}>Product edit history</Typography>
        </AccordionSummary>
        <AccordionDetails className={classes.accordionDetails}>
          {loading && (
            <div className={classes.progressContainer}>
              <CircularProgress size={30} />
            </div>
          )}
          {!loading && error && <div className={classes.errorMessage}>Error loading edit history: {error.message}</div>}
          {!loading && !error && sortedEdits.length === 0 && (
            <div className={classes.emptyMessage}>No edit history found for this product.</div>
          )}
          {!loading && !error && sortedEdits.length > 0 && (
            <div className={classes.historyWrapper}>
              {sortedEdits.map((edit, index) => {
                const nextEdit = sortedEdits[index + 1];
                const date1 = edit.timestamp ? format(Number(edit.timestamp), 'yyyy-MM-dd HH:mm') : '-';
                const date2 =
                  nextEdit && nextEdit.timestamp ? format(Number(nextEdit.timestamp), 'yyyy-MM-dd HH:mm') : '-';
                const type = edit.type;

                const displayJson1 = edit.changes;
                const displayJson2 = edit.previous;
                const changeCount = displayJson1 ? Object.keys(displayJson1).length : 0;

                return (
                  <Accordion
                    key={edit.timestamp}
                    defaultExpanded={true}
                    className={classes.innerAccordion}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography className={classes.editHeading}>{date1}</Typography>
                      <span
                        className={`${classes.badge} ${type === 'CREATE' ? classes.createBadge : classes.updateBadge}`}
                      >
                        {type}
                      </span>
                      <Typography className={classes.changeCount}>
                        {changeCount} {changeCount === 1 ? 'ändring' : 'ändringar'}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                      <Grid container className={classes.rowContainer}>
                        <Grid item xs={6} className={classes.columnLeft}>
                          <div className={classes.headerRow}>
                            <Typography variant="caption" className={classes.headerText}>
                              {date1} (Nuvarande)
                            </Typography>
                          </div>
                          <div className={classes.valueContainer}>
                            {displayJson1 && Object.keys(displayJson1).length > 0 ? (
                              <ReactJson
                                src={displayJson1}
                                enableClipboard={(copy: { src: unknown }) => {
                                  const text =
                                    typeof copy.src === 'string' ? copy.src : JSON.stringify(copy.src, null, 2);
                                  navigator.clipboard.writeText(text);
                                }}
                                iconStyle="triangle"
                                displayDataTypes={false}
                                displayObjectSize={true}
                                indentWidth={4}
                                collapsed={false}
                                name={null}
                                collapseStringsAfterLength={50}
                                quotesOnKeys={false}
                              />
                            ) : (
                              <span className={classes.noChanges}>Inga ändringar</span>
                            )}
                          </div>
                        </Grid>
                        <Grid item xs={6} className={classes.columnRight}>
                          {nextEdit && (
                            <>
                              <div className={classes.headerRow}>
                                <Typography variant="caption" className={classes.headerText}>
                                  {date2} (Tidigare)
                                </Typography>
                              </div>
                              <div className={classes.valueContainer}>
                                {displayJson2 && Object.keys(displayJson2).length > 0 ? (
                                  <ReactJson
                                    src={displayJson2}
                                    enableClipboard={(copy: { src: unknown }) => {
                                      const text =
                                        typeof copy.src === 'string' ? copy.src : JSON.stringify(copy.src, null, 2);
                                      navigator.clipboard.writeText(text);
                                    }}
                                    iconStyle="triangle"
                                    displayDataTypes={false}
                                    displayObjectSize={true}
                                    indentWidth={4}
                                    collapsed={false}
                                    name={null}
                                    collapseStringsAfterLength={50}
                                    quotesOnKeys={false}
                                  />
                                ) : (
                                  <span className={classes.noChanges}>Inga ändringar</span>
                                )}
                              </div>
                            </>
                          )}
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
      {hoveredField && (
        <div
          className={classes.popoverCard}
          style={{
            left: `${hoveredField.x}px`,
            top: `${hoveredField.y}px`,
          }}
        >
          <TooltipContent
            text={hoveredField.text}
            swedishTitle={hoveredField.swedishTitle}
            foodlaCategory={hoveredField.foodlaCategory}
            rootCategory={rootCategory}
            producerIsStore={producerIsStore}
          />
        </div>
      )}
    </div>
  );
};

export default ProductEditHistory;
